<template>
  <!-- Banner Images -->
  <div>
    <div v-if="isBannerLoading" class="sm_visual">
      <!-- 배너 스켈레톤 -->
      <div class="banner-skeleton shimmer"></div>
    </div>

    <div
      class="sm_visual"
      v-else-if="bannerList && bannerList.length > 0"
      ref="smVisual"
      :style="{ height: bannerFixedHeight }"
    >
      <div class="swiper-pagination">
        {{ swiperCurrentIndex + 1 }} / {{ bannerList.length }}
      </div>
      <swiper
        style="height: 100%"
        :options="bannerList.length === 1 ? soloSwiperOption : swiperOption"
        ref="mySwiper"
        @slideChange="handleSwiperChange"
      >
        <swiper-slide v-for="banner in bannerList" :key="banner.id">
          <div
            class="banner-image"
            :style="{
              backgroundImage: `url(${banner.image.resourceUri})`,
            }"
          />
        </swiper-slide>
      </swiper>
    </div>
    <div v-else style="height: 50px"></div>
  </div>
</template>

<script>
import "swiper/dist/css/swiper.css";
import { swiper, swiperSlide } from "vue-awesome-swiper";

import { moveToRubicon } from "@/utils/rubiconUtil";
import { MAIN_BANNER_CONSTANTS } from "@/utils/constants/mainBannerConstants";

export default {
  name: "MainBanner",

  props: ["isBannerLoading", "bannerList"],

  components: { swiper, swiperSlide },

  data() {
    return {
      swiperOption: {
        loop: true,
        autoplay: {
          delay: 5000, // 5초마다
          disableOnInteraction: false,
        },
        slidesPerView: 1,
        spaceBetween: 0,
        on: {
          click: () => {
            this.moveBannerLink();
          },
        },
      },
      soloSwiperOption: {
        slidesPerView: 1,
        spaceBetween: 0,
      },
      swiperCurrentIndex: 0,

      bannerFixedHeight: "200px", // 초기 fallback
    };
  },

  watch: {
    bannerList(newVal) {
      if (newVal && newVal.length > 0) {
        this.$nextTick(() => {
          const firstBanner = newVal[0];
          const heightRate = this.handleBannerHeightRate(firstBanner);
          this.bannerFixedHeight = heightRate;
        });
      }
    },
  },

  mounted() {
    window.addEventListener("resize", this.handleResize);
  },

  beforeDestroy() {
    window.removeEventListener("resize", this.handleResize);
  },

  methods: {
    handleSwiperChange() {
      this.swiperCurrentIndex = this.$refs.mySwiper.swiper.realIndex;
    },

    moveBannerLink() {
      // 2025-09-29 캠페인 배너 API로 동적 이동 처리
      const bannerItem =
        this.bannerList && this.bannerList[this.swiperCurrentIndex];

      if (!bannerItem) {
        console.warn("배너 아이템이 존재하지 않습니다.");
        return;
      }

      const linkType =
        (bannerItem.linkType &&
          bannerItem.linkType.value &&
          bannerItem.linkType.value.toUpperCase()) ||
        null;
      const linkPath =
        (bannerItem.linkPath && bannerItem.linkPath.value) || null;
      const linkIdx = bannerItem.linkIdx;
      const linkBoardIdentifier = bannerItem.linkBoardIdentifier;

      console.group(" >>> bannerItem");
      console.log("bannerItem : ", bannerItem);
      console.log("linkType : ", linkType);
      console.log("linkPath : ", linkPath);
      console.log("linkIdx : ", linkIdx);
      console.log("linkBoardIdentifier : ", linkBoardIdentifier);
      console.groupEnd();

      if (!linkPath) return;

      // 라우팅 맵핑 객체를 통한 분기 처리
      const routeHandlers = {
        // 260629 hide rubicon menu
        // [MAIN_BANNER_CONSTANTS.RUBICON]: () => moveToRubicon(this),
        [MAIN_BANNER_CONSTANTS.STAR_RADAR]: () =>
          this.handleStarRadarRoute(linkType, linkIdx),
        [MAIN_BANNER_CONSTANTS.FORUM]: () =>
          this.handleForumRoute(linkType, linkIdx, linkBoardIdentifier),
        [MAIN_BANNER_CONSTANTS.SURVEY]: () =>
          this.handleSurveyRoute(linkType, linkIdx),
        [MAIN_BANNER_CONSTANTS.EVENT]: () =>
          this.handleEventRoute(linkType, linkIdx),
        [MAIN_BANNER_CONSTANTS.EXAM]: () =>
          this.handleExamRoute(linkType, linkIdx),
        [MAIN_BANNER_CONSTANTS.CONTENT]: () =>
          this.handleLearningRoute(linkIdx, MAIN_BANNER_CONSTANTS.CONTENTS),
        [MAIN_BANNER_CONSTANTS.E_LEARNING]: () =>
          this.handleLearningRoute(linkIdx, MAIN_BANNER_CONSTANTS.LEARNING),
      };

      const handler = routeHandlers[linkPath];
      if (handler) {
        return handler();
      }

      console.warn(`처리되지 않은 linkPath: ${linkPath}`);
    },

    // Star Radar 라우팅 처리
    handleStarRadarRoute(linkType, linkIdx) {
      if (linkType === MAIN_BANNER_CONSTANTS.DETAIL) {
        return this.$router.push(`/radar/${linkIdx}`);
      }
      return this.$router.push("/radar");
    },

    // Forum 라우팅 처리
    handleForumRoute(linkType, linkIdx, linkBoardIdentifier) {
      if (linkType === MAIN_BANNER_CONSTANTS.DETAIL) {
        return this.$router.push(
          `/socialLearning/${linkIdx}?bId=${linkBoardIdentifier}`
        );
      }
      return this.$router.push(
        `/socialLearning?bId=${linkBoardIdentifier || "SNS"}`
      );
    },

    // Survey 라우팅 처리
    handleSurveyRoute(linkType, linkIdx) {
      if (linkType === MAIN_BANNER_CONSTANTS.DETAIL) {
        return this.$router.push(`/independent/survey/${linkIdx}`);
      }
      return this.$router.push(`/independent/survey`);
    },

    // Event 라우팅 처리
    handleEventRoute(linkType, linkIdx) {
      if (linkType === MAIN_BANNER_CONSTANTS.DETAIL) {
        return this.$router.push(`/event/${linkIdx}?board=BOARD1`);
      }

      return this.$router.push(`/event`);
    },

    // Exam 라우팅 처리
    handleExamRoute(linkType, linkIdx) {
      if (linkType === MAIN_BANNER_CONSTANTS.DETAIL) {
        return this.$router.push(`/independent/exam/${linkIdx}`);
      }
      return this.$router.push(`/independent/exam`);
    },

    // Learning 라우팅 처리
    handleLearningRoute(linkIdx, status) {
      const groupCategoryId =
        status === MAIN_BANNER_CONSTANTS.CONTENTS
          ? sessionStorage.getItem(MAIN_BANNER_CONSTANTS.CONTENTS)
          : sessionStorage.getItem(MAIN_BANNER_CONSTANTS.E_LEARNING);

      return this.$router.push({
        path: `learn?status=${status}&category=${linkIdx}&groupCategoryId=${groupCategoryId}`,
      });
    },

    handleBannerHeightRate(firstBanner) {
      if (
        !firstBanner.image ||
        !firstBanner.image.height ||
        !firstBanner.image.width
      )
        return "200px";
      const imageHeight = firstBanner.image.height;
      const imageWidth = firstBanner.image.width;
      const deviceWidth = window.innerWidth;
      const heightRate = (imageHeight * deviceWidth) / imageWidth;
      return `${heightRate}px`;
    },

    handleResize() {
      if (this.bannerList && this.bannerList.length > 0) {
        const firstBanner = this.bannerList[0];
        const heightRate = this.handleBannerHeightRate(firstBanner);
        this.bannerFixedHeight = heightRate;
      }
    },
  },
};
</script>

<style scoped>
.sm_visual {
  position: relative;
  height: 200px;
  transition: height 0.3s ease-in-out;
}
.sm_visual img {
  display: block;
  width: 100%;
}

.banner-image {
  height: 100%;
  background-size: cover;
  background-position: center;
}
.swiper-pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  width: 40px;
  height: 20px;
  padding: 3px;
  font-size: 10px;
  color: white;
  background: rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(20px);
  position: absolute;
  top: 10px;
  right: 10px;
}

.banner-skeleton {
  height: 200px;
  width: 100%;
  background-color: #f0f0f0;
  border-radius: 4px;
}

.shimmer {
  position: relative;
  overflow: hidden;
  background-color: #e0e0e0;
}

.shimmer::after {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.6) 50%,
    rgba(255, 255, 255, 0) 100%
  );
  transform: translateX(-100%);
  animation: shimmer 1.5s ease-in-out infinite;
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}
</style>
